<!--
  DETTAGLIO PRENOTAZIONE.

  Due colonne: le TRATTE a sinistra, dove si legge cosa succede, il riepilogo
  a destra, come nel gestionale admin, con due differenze volute per il partner:

    - l'AUTO è la prima card del riepilogo, non il cliente: qui l'informazione
      che identifica il lavoro è il mezzo;
    - driver e jolly sono in SOLA LETTURA. Il partner non li assegna — lo fa
      Simba — ma deve sapere chi arriva e poterlo chiamare.

  ⚠️ UN BOOKING HA PIÙ TRATTE, in tre collezioni diverse a seconda del
  servizio (pickup_bookings / escort_bookings / delivery_bookings), e i punti
  si chiamano in modo diverso in ognuna. Sul PICKUP la tratta ha un capo solo
  (l'indirizzo del cliente): l'altro è sempre la sede.

  ⚠️ `planner.jolly` NON è il jolly: è il driver assegnato a quel planner. Il
  ruolo lo dice `planner.type`. Vedi `agendaAPI.bookingDetail`.
-->
<template>
  <div
    v-loading="loading"
    class="det"
  >
    <!-- ---------- intestazione ---------- -->
    <div class="det__head">
      <button
        type="button"
        class="det__back"
        aria-label="Torna indietro"
        @click="indietro"
      >
        ‹
      </button>
      <div>
        <div class="det__titleRow">
          <h1 class="det__title">
            Prenotazione #{{ shortId }}
          </h1>
          <StatusPill
            :kind="stato.kind"
            :label="stato.label"
          />
        </div>
        <div class="det__sub">
          <ServiceChip :service="servizio" />
          <span>{{ sedeLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Lo stato per esteso: la pastiglia dice la famiglia, qui il passo. -->
    <div
      v-if="statoEsteso"
      class="det__now"
    >
      <span class="det__dot" />
      {{ statoEsteso }}
    </div>

    <div class="det__grid">
      <!-- ================= TRATTE ================= -->
      <div class="det__col">
        <section
          v-for="t in tratte"
          :key="t.key"
          class="det__card"
        >
          <header class="det__cardHead">
            <h3>{{ t.titolo }}</h3>
            <span class="det__legTag">{{ t.direzione === 'gone' ? 'Andata' : 'Ritorno' }}</span>
          </header>

          <div class="det__cardBody">
            <div class="det__legTop">
              <div>
                <div class="det__whenD">
                  {{ giorno(t.start) }}
                </div>
                <div class="det__whenH">
                  {{ ora(t.start) }}<template v-if="t.end"> → {{ ora(t.end) }}</template>
                </div>
              </div>

              <div class="det__route">
                <div class="det__pt">
                  <span class="det__pin det__pin--from" />
                  <span class="det__addr">{{ t.from || '—' }}</span>
                </div>
                <div class="det__pt">
                  <span class="det__pin det__pin--to" />
                  <span class="det__addr">{{ t.to || '—' }}</span>
                </div>
              </div>

              <div
                v-if="t.km"
                class="det__km"
              >
                <div class="det__kmN">
                  {{ t.km }}
                </div>
                <div class="det__kmL">
                  km
                </div>
              </div>
            </div>

            <div class="det__sep" />

            <!-- Chi arriva. In sola lettura: l'assegnazione la fa Simba. -->
            <div class="det__people">
              <div
                v-for="p in t.persone"
                :key="p.ruolo"
                class="det__person"
              >
                <span
                  class="det__ava"
                  :class="{ 'det__ava--empty': !p.nome }"
                >{{ p.nome ? iniziali(p.nome) : '—' }}</span>
                <div>
                  <div
                    class="det__personName"
                    :class="{ 'det__personName--empty': !p.nome }"
                  >
                    {{ p.nome || 'Non assegnato' }}
                  </div>
                  <div class="det__meta">
                    {{ p.ruolo }}<template v-if="p.telefono"> · {{ p.telefono }}</template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ---- lavorazioni ---- -->
        <section
          v-if="lavorazioni.length"
          class="det__card"
        >
          <header class="det__cardHead">
            <h3>Lavorazioni</h3>
            <span class="det__hint">quello che il cliente vede — e paga — dall'app</span>
          </header>
          <div class="det__taskHead">
            <span class="det__th">Descrizione</span>
            <span class="det__th det__right">Importo</span>
            <span class="det__th det__right">Pagamento</span>
          </div>
          <div
            v-for="l in lavorazioni"
            :key="l.id"
            class="det__task"
          >
            <span>{{ l.description || '—' }}</span>
            <span class="det__right det__num">{{ euro(l.cost) }}</span>
            <span class="det__right">
              <StatusPill
                :kind="l.accepted ? 'ok' : 'attn'"
                :label="l.accepted ? 'Pagata in app' : 'Da saldare'"
              />
            </span>
          </div>
        </section>

        <!-- ---- note ---- -->
        <section
          v-if="booking.note"
          class="det__card"
        >
          <header class="det__cardHead">
            <h3>Note</h3>
          </header>
          <div class="det__cardBody">
            <p class="det__note">
              {{ booking.note }}
            </p>
          </div>
        </section>
      </div>

      <!-- ================= RIEPILOGO ================= -->
      <div class="det__aside">
        <!-- L'auto per prima: è l'informazione che identifica il lavoro. -->
        <section class="det__card">
          <header class="det__cardHead">
            <h3>{{ mezzo.titolo }}</h3>
          </header>
          <div class="det__cardBody">
            <div class="det__plate">
              {{ mezzo.plate || '—' }}
            </div>
            <div
              v-if="mezzo.label"
              class="det__carLabel"
            >
              {{ mezzo.label }}
            </div>
            <div
              v-if="mezzo.extra"
              class="det__meta"
            >
              {{ mezzo.extra }}
            </div>

            <div
              v-if="booking.loaner"
              class="det__cort"
            >
              <div class="det__cortLab">
                Auto di cortesia
              </div>
              <div class="det__cortPlate">
                {{ booking.targa_loaner || '—' }}
              </div>
              <!-- Del mezzo di cortesia il booking conserva solo la targa:
                   marca e modello non esistono a database. -->
            </div>
          </div>
        </section>

        <section
          v-if="booking.customer"
          class="det__card"
        >
          <header class="det__cardHead">
            <h3>Cliente</h3>
          </header>
          <div class="det__cardBody">
            <div class="det__custRow">
              <span class="det__ava">{{ iniziali(clienteNome) }}</span>
              <span class="det__custName">{{ clienteNome }}</span>
            </div>
            <div class="det__kv">
              <span class="det__k">Telefono</span>
              <span class="det__v det__num">{{ booking.customer.telephone_number || '—' }}</span>
            </div>
            <div class="det__kv det__kv--last">
              <span class="det__k">Email</span>
              <span class="det__v">{{ booking.customer.mail || '—' }}</span>
            </div>
          </div>
        </section>

        <section class="det__card">
          <header class="det__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="det__cardBody">
            <div class="det__kv">
              <span class="det__k">Sede</span>
              <span class="det__v">{{ sedeLabel }}</span>
            </div>
            <div class="det__kv">
              <span class="det__k">Tratte</span>
              <span class="det__v">{{ tratteLabel }}</span>
            </div>
            <!-- Con le tariffe del partner, calcolati al momento. Sul
                 booking restano salvati quelli Simba. -->
            <div class="det__kv">
              <span class="det__k">Imponibile</span>
              <span class="det__v det__num">{{ importo(prezzoPartner ? prezzoPartner.imponibile : null) }}</span>
            </div>
            <!-- La benzina fatta dal driver al tuo veicolo: un rimborso spese
                 a parte, senza IVA sopra. La scrive Simba. -->
            <div
              v-if="benzina"
              class="det__kv"
            >
              <span class="det__k">Benzina</span>
              <span class="det__v det__num">{{ euro(benzina) }}</span>
            </div>
            <div class="det__kv det__kv--last det__kv--total">
              <span class="det__k det__kTotal">{{ benzina ? 'Totale con la benzina' : 'Totale' }}</span>
              <span class="det__total">{{ importo(totaleConBenzina) }}</span>
            </div>
            <p
              v-if="notaPrezzo"
              class="det__meta"
              aria-live="polite"
            >
              {{ notaPrezzo }}
            </p>
          </div>
        </section>

        <!-- ---- allegati ----
             Scontrini, foto, documenti del lavoro. I file stanno sull'object
             storage e qui arrivano link temporanei; li vede anche Simba. -->
        <section class="det__card">
          <header class="det__cardHead">
            <h3>Allegati</h3>
            <span class="det__count">{{ allegati.length }}</span>
            <span class="det__spacer" />
            <label
              class="det__upload"
              :class="{ 'is-busy': caricamento }"
            >
              <input
                type="file"
                class="det__file"
                :disabled="caricamento"
                @change="caricaAllegato"
              >
              {{ caricamento ? 'Carico…' : '+ Aggiungi' }}
            </label>
          </header>
          <div class="det__cardBody">
            <p
              v-if="erroreAllegati"
              class="det__meta"
            >
              Non riesco a leggere gli allegati.
            </p>
            <p
              v-else-if="!allegati.length"
              class="det__meta"
            >
              Nessun allegato.
            </p>
            <div
              v-for="a in allegati"
              :key="a.id"
              class="det__att"
            >
              <div class="det__attText">
                <a
                  :href="a.url"
                  target="_blank"
                  rel="noopener"
                  class="det__attName"
                >{{ a.file_name }}</a>
                <span class="det__meta">
                  {{ dimensione(a.size_bytes) }} · {{ dataBreve(a.created_at) }}<template v-if="a.uploaded_by === 'driver'"> · dal driver</template>
                </span>
              </div>
              <button
                type="button"
                class="det__attDel"
                :aria-label="`Elimina ${a.file_name}`"
                @click="eliminaAllegato(a)"
              >
                ×
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  bookingDetail, allegatiPrenotazione, nuovoAllegato, cancellaAllegato, prezzoPartnerPrenotazione,
} from '@/services/agendaAPI';
import { bookingState } from '@/services/agendaRows';
import { VEHICLE_LABELS } from '@/services/vehicleTypes';
import ServiceChip from '@/Components/ui/ServiceChip.vue';
import StatusPill from '@/Components/ui/StatusPill.vue';

// Il passo esatto della macchina a stati, per esteso. La pastiglia dice la
// famiglia («In corso»); qui si dice cosa sta succedendo davvero.
const PASSI = {
  before: 'Non ancora confermata',
  accepted: 'Accettata dal cliente',
  end: 'Conclusa',
  canceled: 'Annullata',
  startAPickup: 'Andata · driver in viaggio',
  customerAPickup: 'Andata · driver arrivato dal cliente',
  photoAPickup: 'Andata · il driver ha fatto le foto',
  scanAPickup: "Andata · il driver ha ritirato l'auto",
  partnerAPickup: 'Andata · driver arrivato in sede',
  startRPickup: 'Ritorno · driver in viaggio',
  partnerRPickup: 'Ritorno · driver arrivato in sede',
  customerRPickup: 'Ritorno · driver arrivato dal cliente',
  scanRPickup: "Ritorno · il driver ha consegnato l'auto",
  startAEscort: 'Andata · driver in viaggio',
  customerAEscort: 'Andata · driver al punto di incontro',
  scanfirstAEscort: 'Andata · cliente salito in auto',
  scanssecondAEscort: 'Andata · cliente portato a destinazione',
  partnerAEscort: 'Andata · driver arrivato in sede',
  startREscort: 'Ritorno · driver in viaggio',
  customerREscort: 'Ritorno · driver al punto di incontro',
  scanfirstREscort: 'Ritorno · cliente salito in auto',
  scansecondREscort: 'Ritorno · cliente portato a destinazione',
};

// Indirizzo COMPLETO: via e civico, CAP, città e provincia
// («Via Roma 12, 20121 Milano (MI)»).
const place = (p) => {
  if (!p) return '';
  const via = [p.address, p.street_number].filter(Boolean).join(' ');
  const citta = [p.cap, p.city, p.province ? `(${p.province})` : ''].filter(Boolean).join(' ');
  return [via, citta].filter(Boolean).join(', ').trim();
};
const nome = (d) => (d ? [d.name, d.surname].filter(Boolean).join(' ').trim() : '');

export default {
  name: 'DettaglioPrenotazionePage',
  components: { ServiceChip, StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const booking = ref({});
    const loading = ref(false);

    const carica = async () => {
      loading.value = true;
      try {
        const r = await bookingDetail(route.params.id);
        booking.value = r.data || {};
      } catch (e) {
        console.error('Prenotazione non caricata:', e);
        ElMessage({ message: 'Prenotazione non caricata', type: 'error' });
      } finally {
        loading.value = false;
      }
    };
    onMounted(carica);

    // ---- prezzo con le tariffe del partner --------------
    // Sul booking restano salvati imponibile e totale di Simba, cioè quanto
    // Simba fattura al partner. Qui si mostra quanto paga il cliente: il
    // backend lo calcola al momento con le tariffe del partner.
    const prezzoPartner = ref(null);
    const statoPrezzo = ref('carico'); // carico | ok | errore
    const caricaPrezzo = async () => {
      statoPrezzo.value = 'carico';
      try {
        const r = await prezzoPartnerPrenotazione(route.params.id);
        prezzoPartner.value = r.data || null;
        statoPrezzo.value = 'ok';
      } catch (e) {
        console.error('Prezzo con le tariffe del partner non calcolato:', e);
        statoPrezzo.value = 'errore';
      }
    };
    onMounted(caricaPrezzo);

    const notaPrezzo = computed(() => {
      if (statoPrezzo.value === 'carico') return 'Calcolo il prezzo con le tariffe del partner…';
      if (statoPrezzo.value === 'errore') return 'Non riesco a calcolare il prezzo con le tariffe del partner.';
      const p = prezzoPartner.value;
      if (!p) return '';
      if (!p.complete) {
        // Col valet il prezzo manca anche quando il venditore non ha il €/km dei
        // km oltre il pacchetto (`valet.excess_price` null): va detto quello.
        if (p.valet && p.valet.excess_price === null) {
          return `Corsa coperta dal pacchetto valet, ma ${String(p.valet.excess_km).replace('.', ',')} km sono oltre quelli compresi e il prezzo al km dei km in più non è impostato: si imposta nella pagina Tariffe.`;
        }
        return 'Prezzo non calcolabile: manca la distanza di una tratta o la tariffa del partner per questo servizio. Le tariffe si impostano nella pagina Tariffe.';
      }
      if (p.valet) {
        return p.valet.excess_km
          ? `Corsa coperta dal pacchetto valet: si pagano solo i ${String(p.valet.excess_km).replace('.', ',')} km oltre quelli compresi.`
          : 'Corsa coperta dal pacchetto valet.';
      }
      return 'Calcolati con le tariffe del partner in vigore il giorno del servizio.';
    });

    const indietro = () => router.back();

    const shortId = computed(() => (booking.value.id || '').slice(0, 8));
    const notturno = computed(() => booking.value.type_booking === 'restaurant');
    const stato = computed(() => bookingState(booking.value.state));
    const statoEsteso = computed(() => PASSI[booking.value.state] || '');
    const sedeLabel = computed(
      () => booking.value.headquarter?.name || place(booking.value.headquarter) || '—'
    );
    const clienteNome = computed(() => nome(booking.value.customer) || 'Cliente');
    const lavorazioni = computed(() => booking.value.tasks || []);

    const servizio = computed(() => {
      if (notturno.value) return 'restaurant';
      if ((booking.value.escort_bookings || []).length) return 'escort';
      return booking.value.loaner ? 'pickup_loaner' : 'pickup';
    });

    // Il mezzo: l'auto del cliente sul pickup, il veicolo scelto sul transfer.
    // Stessa distinzione dell'elenco (vedi `agendaRows.mezzoDi`).
    const mezzo = computed(() => {
      const c = booking.value.car;
      if (c?.targa) {
        return {
          titolo: 'Auto',
          plate: c.targa,
          // `Car` non ha un campo colore: marca e modello sono tutto quello che c'è.
          label: [c.marca, c.modello].filter(Boolean).join(' '),
          extra: [
            c.transmission === 'manual' ? 'Cambio manuale' : '',
            c.transmission === 'automatic' ? 'Cambio automatico' : '',
            c.intestatario ? `intestata a ${c.intestatario}` : '',
          ].filter(Boolean).join(' · '),
        };
      }
      return {
        titolo: 'Mezzo',
        plate: VEHICLE_LABELS[booking.value.vehicle_type] || '—',
        label: '',
        extra: booking.value.passengers
          ? `${booking.value.passengers} passegger${booking.value.passengers === 1 ? 'o' : 'i'}`
          : '',
      };
    });

    // Le persone di una tratta: il driver della corsa, più chi è assegnato ai
    // planner. `planner.jolly` è la PERSONA, `planner.type` il ruolo.
    const personeDi = (leg, driverDiretto) => {
      const perRuolo = { driver: null, jolly: null };
      (leg.planners || []).forEach((pl) => {
        const r = pl.type === 'jolly' ? 'jolly' : 'driver';
        if (!perRuolo[r] && pl.jolly) perRuolo[r] = pl.jolly;
      });
      const driver = perRuolo.driver || driverDiretto || null;
      const out = [
        { ruolo: 'Driver', nome: nome(driver), telefono: driver?.telephone_number || '' },
      ];
      // Il jolly si mostra solo se la corsa ne prevede uno: un posto vuoto su
      // una tratta che non lo richiede sembrerebbe una mancanza.
      if (perRuolo.jolly) {
        out.push({
          ruolo: 'Jolly',
          nome: nome(perRuolo.jolly),
          telefono: perRuolo.jolly.telephone_number || '',
        });
      }
      return out;
    };

    const tratte = computed(() => {
      const b = booking.value;
      const sede = sedeLabel.value;
      const out = [];

      (b.pickup_bookings || []).forEach((leg, i) => {
        const andata = leg.type_booking !== 'return';
        const casa = place(leg.point);
        out.push({
          key: `p${i}`,
          titolo: notturno.value ? 'Tratta notturno' : 'Tratta pickup',
          direzione: andata ? 'gone' : 'return',
          start: andata ? leg.date_customer : leg.date_partner,
          end: andata ? leg.date_partner : leg.date_customer,
          from: andata ? casa : sede,
          to: andata ? sede : casa,
          km: leg.simba_km ?? null,
          persone: personeDi(leg, leg.driver_pickup),
        });
      });

      (b.escort_bookings || []).forEach((leg, i) => {
        const andata = leg.type_booking !== 'return';
        out.push({
          key: `e${i}`,
          titolo: 'Tratta transfer',
          direzione: andata ? 'gone' : 'return',
          start: andata ? leg.date_customer : leg.date_partner,
          end: andata ? leg.date_partner : leg.date_customer,
          from: place(leg.point_origin_escort),
          to: place(leg.point_destination_escort),
          km: leg.simba_km ?? null,
          persone: personeDi(leg, leg.driver_escort),
        });
      });

      (b.delivery_bookings || []).forEach((leg, i) => {
        const andata = leg.type_booking !== 'return';
        out.push({
          key: `d${i}`,
          titolo: 'Tratta consegna',
          direzione: andata ? 'gone' : 'return',
          start: andata ? leg.date_customer : leg.date_partner,
          end: andata ? leg.date_partner : leg.date_customer,
          from: place(leg.point_origin_delivery),
          to: place(leg.point_destination_delivery),
          km: leg.simba_km ?? null,
          persone: personeDi(leg, leg.driver_delivery),
        });
      });

      // Le tre collezioni tornano in ordine arbitrario: una pagina di dettaglio
      // si legge in ordine CRONOLOGICO, altrimenti il ritorno può comparire prima
      // dell'andata. Le tratte senza orario restano in fondo.
      const quando = (x) => {
        const n = x.start ? new Date(x.start).getTime() : NaN;
        return Number.isNaN(n) ? Infinity : n;
      };
      return out.sort((a, b) => quando(a) - quando(b));
    });

    const tratteLabel = computed(() => {
      const n = tratte.value.length;
      if (!n) return 'Nessuna';
      if (n === 1) return tratte.value[0].direzione === 'gone' ? 'Solo andata' : 'Solo ritorno';
      return `${n} tratte`;
    });

    // ---- allegati --------------------------------------------------------
    const allegati = ref([]);
    const erroreAllegati = ref(false);
    const caricamento = ref(false);
    // Il backend accetta richieste grandi, ma un file enorme in base64 rende
    // la pagina lenta per chiunque lo carichi: si ferma prima.
    const MAX_MB = 20;

    const caricaAllegati = async () => {
      try {
        const r = await allegatiPrenotazione(route.params.id);
        allegati.value = Array.isArray(r.data) ? r.data : [];
        erroreAllegati.value = false;
      } catch (e) {
        console.error('Allegati non letti:', e);
        erroreAllegati.value = true;
      }
    };
    onMounted(caricaAllegati);

    const leggiFile = (file) => new Promise((resolve, reject) => {
      const lettore = new FileReader();
      lettore.onload = () => resolve(lettore.result);
      lettore.onerror = () => reject(lettore.error);
      lettore.readAsDataURL(file);
    });

    const caricaAllegato = async (evento) => {
      const file = evento.target.files && evento.target.files[0];
      evento.target.value = '';
      if (!file) return;
      if (file.size > MAX_MB * 1024 * 1024) {
        ElMessage({ type: 'warning', message: `Il file supera i ${MAX_MB} MB.` });
        return;
      }
      caricamento.value = true;
      try {
        const dataUri = await leggiFile(file);
        await nuovoAllegato(route.params.id, { file: dataUri, fileName: file.name });
        ElMessage({ type: 'success', message: 'Allegato caricato.' });
        await caricaAllegati();
      } catch (e) {
        console.error('Allegato non caricato:', e);
        ElMessage({ type: 'error', message: 'Allegato non caricato. Riprova.' });
      } finally {
        caricamento.value = false;
      }
    };

    const eliminaAllegato = async (a) => {
      try {
        await ElMessageBox.confirm(`Eliminare «${a.file_name}»?`, 'Elimina allegato', {
          confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning',
        });
      } catch (e) {
        return; // annullato
      }
      try {
        await cancellaAllegato(route.params.id, a.id);
        ElMessage({ type: 'success', message: 'Allegato eliminato.' });
        await caricaAllegati();
      } catch (e) {
        console.error('Allegato non eliminato:', e);
        ElMessage({ type: 'error', message: 'Allegato non eliminato. Riprova.' });
      }
    };

    const dimensione = (b) => {
      if (!b) return '—';
      if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
      return `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
    };
    const dataBreve = (v) => (v ? new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

    const ora = (v) => (v ? new Date(v).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—');
    const giorno = (v) => (v ? new Date(v).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) : '');
    const euro = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    // Un importo che non si può calcolare è «—», non «€ 0,00».
    const importo = (n) => (n === null || n === undefined ? '—' : euro(n));
    const iniziali = (s) => s.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

    // Benzina (rimborso spese a parte): si somma al totale, fuori dall'IVA.
    const benzina = computed(() => Number(booking.value.carburante || 0));
    const totaleConBenzina = computed(() => {
      const t = prezzoPartner.value ? prezzoPartner.value.tot_fattura : null;
      if (t === null || t === undefined) return benzina.value ? null : t;
      return Math.round((Number(t) + benzina.value) * 100) / 100;
    });

    return {
      benzina, totaleConBenzina,
      booking, loading, indietro, shortId, stato, statoEsteso, servizio,
      sedeLabel, clienteNome, lavorazioni, mezzo, tratte, tratteLabel,
      ora, giorno, euro, importo, iniziali, prezzoPartner, notaPrezzo,
      allegati, erroreAllegati, caricamento, caricaAllegato, eliminaAllegato, dimensione, dataBreve,
    };
  },
};
</script>

<style scoped>
.det { padding: 4px 8px 32px; text-align: left; }

.det__head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 12px; }
.det__back { width: 30px; height: 30px; border: 1px solid var(--color-border); background: #fff; border-radius: 6px; font-size: 15px; color: var(--color-text-secondary); cursor: pointer; flex: none; }
.det__titleRow { display: flex; align-items: center; gap: 11px; }
.det__title { margin: 0; font-size: 23px; font-weight: 700; letter-spacing: -0.015em; }
.det__sub { display: flex; align-items: center; gap: 9px; margin-top: 5px; font-size: 12.5px; color: var(--color-text-secondary); }

.det__now { display: flex; align-items: center; gap: 9px; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 11px 16px; font-size: 13px; font-weight: 600; margin-bottom: 16px; }
.det__dot { width: 8px; height: 8px; border-radius: 50%; background: var(--state-active); flex: none; }

.det__grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 20px; align-items: start; }
.det__col, .det__aside { display: flex; flex-direction: column; gap: 16px; }

.det__card { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
.det__cardHead { display: flex; align-items: center; gap: 11px; padding: 13px 18px; border-bottom: 1px solid var(--color-border); }
.det__cardHead h3 { margin: 0; font-size: 14.5px; font-weight: 600; }
.det__cardBody { padding: 16px 18px; }
.det__hint { font-size: 12px; color: var(--color-text-muted); }
.det__legTag { font-size: 11px; font-weight: 650; color: var(--state-muted); background: var(--state-muted-bg); border-radius: 20px; padding: 1px 9px; }

.det__legTop { display: grid; grid-template-columns: 190px minmax(0, 1fr) 74px; gap: 22px; align-items: start; }
.det__whenD { font-size: 11.5px; font-weight: 650; color: var(--color-text-secondary); }
.det__whenH { font-size: 17px; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
.det__route { display: flex; flex-direction: column; gap: 7px; }
.det__pt { display: flex; align-items: flex-start; gap: 9px; }
.det__pin { width: 9px; height: 9px; border-radius: 50%; margin-top: 5px; flex: none; }
.det__pin--from { background: var(--color-text-muted); }
.det__pin--to { background: var(--color-brand); }
.det__addr { font-size: 13px; line-height: 1.45; }
.det__km { text-align: right; }
.det__kmN { font-size: 19px; font-weight: 700; font-variant-numeric: tabular-nums; }
.det__kmL { font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-muted); }

.det__sep { height: 1px; background: var(--color-bg-tertiary); margin: 15px 0 13px; }
.det__people { display: flex; align-items: center; gap: 26px; flex-wrap: wrap; }
.det__person { display: flex; align-items: center; gap: 9px; }
.det__ava { width: 26px; height: 26px; border-radius: 50%; background: var(--state-muted-bg); color: var(--state-muted); display: flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 700; flex: none; }
.det__ava--empty { background: #fff; border: 1px dashed var(--el-border-color-dark); color: var(--color-text-muted); }
.det__personName { font-size: 13px; font-weight: 600; }
.det__personName--empty { font-weight: 400; color: var(--color-text-muted); }
.det__meta { font-size: 12px; color: var(--color-text-muted); }

.det__taskHead, .det__task { display: grid; grid-template-columns: minmax(0, 1fr) 130px 140px; gap: 16px; align-items: center; padding: 12px 18px; }
.det__taskHead { background: var(--color-bg-secondary); border-bottom: 1px solid var(--color-border); padding: 10px 18px; }
.det__task { border-bottom: 1px solid var(--color-bg-tertiary); font-size: 13px; }
.det__task:last-child { border-bottom: 0; }
.det__th { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--color-text-secondary); }
.det__right { text-align: right; }
.det__num { font-variant-numeric: tabular-nums; }
.det__note { margin: 0; font-size: 13px; line-height: 1.6; color: var(--color-text-secondary); }

.det__plate { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; }
.det__carLabel { font-size: 13.5px; font-weight: 600; margin-top: 3px; }
.det__cort { margin-top: 13px; padding-left: 11px; border-left: 2px solid var(--color-border); }
.det__cortLab { font-size: 9.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-muted); }
.det__cortPlate { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; margin-top: 2px; }

.det__custRow { display: flex; align-items: center; gap: 11px; margin-bottom: 12px; }
.det__custName { font-size: 14.5px; font-weight: 600; }
.det__kv { display: flex; align-items: baseline; justify-content: space-between; gap: 14px; padding: 7px 0; border-bottom: 1px solid var(--color-bg-tertiary); }
.det__kv--last { border-bottom: 0; }
.det__kv--total { padding-top: 11px; }
.det__k { font-size: 12.5px; color: var(--color-text-secondary); }
.det__kTotal { font-size: 13px; font-weight: 600; color: var(--color-text-primary); }
.det__v { font-size: 13px; font-weight: 600; text-align: right; }
.det__total { font-size: 17px; font-weight: 700; font-variant-numeric: tabular-nums; }

@media (max-width: 1100px) {
  .det__grid { grid-template-columns: minmax(0, 1fr); }
}

/* ---- allegati ---- */
.det__count { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--color-text-secondary); background: var(--color-bg-tertiary); border-radius: 999px; padding: 1px 8px; }
.det__spacer { flex: 1 1 auto; }
.det__upload { position: relative; font-size: 12.5px; font-weight: 600; color: var(--color-primary); border: 1px solid var(--color-border); border-radius: 6px; padding: 5px 11px; cursor: pointer; }
.det__upload:hover { border-color: var(--color-primary); }
.det__upload:focus-within { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.det__upload.is-busy { opacity: 0.6; cursor: default; }
.det__file { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.det__att { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--color-bg-tertiary); }
.det__att:last-child { border-bottom: 0; }
.det__attText { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
.det__attName { font-size: 13px; font-weight: 600; color: var(--color-text-primary); overflow-wrap: anywhere; text-decoration: none; }
.det__attName:hover { color: var(--color-primary); text-decoration: underline; }
.det__attDel { flex: none; width: 26px; height: 26px; border: 1px solid var(--color-border); border-radius: 6px; background: #fff; color: var(--state-danger); font-size: 15px; line-height: 1; cursor: pointer; }
.det__attDel:hover { border-color: var(--state-danger); }
</style>
